// Library imports
import React from 'react';
import { bool, number, shape, string } from 'prop-types';
import { useSelector } from 'react-redux';
// Component imports
import CompareList from '../../../../components/CompareList';
// Util imports
import { getGameTeamData } from '../../utils';
// Styled components
import { InnerContent, NoDataMessage } from '../styles';
import {
    TotalContainer,
    TotalText,
    TotalLabel,
    PredictionCard,
    PredictionBadge,
    PredictionLabel,
    StatsRow,
    ProgressBarContainer,
    SectionLabel,
    ProgressLabels,
    ProgressLabel,
    LabelText,
    LabelValue,
    LabelPercentage,
    ProgressBar,
    ProgressFill,
    LineMovementContainer,
    LineMovementTitle,
    LineMovementRow,
    LineMovementValue,
    LineMovementNumber,
    LineMovementLabel,
    LineMovementArrow,
    DeltaBadge,
    MovementIcon,
    RankHint,
} from './styles';

const Totals = ({ sport, game, demo, isLocked = false, unlockCredits = 1 }) => {
    // Redux
    const { teams } = useSelector(state => state.app);
    const { awayTeamData, homeTeamData } = getGameTeamData(teams, game, demo, sport);

    const generateFieldList = team => {
        if (!team?.off || !team?.recordData) {
            return [];
        }
        const teamStats = [];
        teamStats.push({ label: 'PPG', value: team.off?.oppg ? team.off?.oppg : 'N/A' });
        teamStats.push({ label: 'O/U', value: team.recordData?.ou ? team.recordData?.ou : 'N/A' });
        if (sport === 'mlb') {
            return teamStats;
        }
        if (sport === 'nba' || sport === 'cbb') {
            teamStats.push({ label: 'Pace Rank', value: team.off?.paceofplay ? `#${team.off.paceofplay}` : 'N/A' });
        } else {
            teamStats.push({ label: 'TOP Rank', value: team.off?.paceofplay ? `#${team.off.paceofplay}` : 'N/A' });
        }
        return teamStats;
    };

    if (isLocked) {
        return (
            <InnerContent fluid>
                <div
                    style={{
                        margin: '0.55rem',
                        padding: '1.05rem 1rem',
                        border: '1px dashed rgba(250,204,21,0.45)',
                        borderRadius: 12,
                        background: 'linear-gradient(160deg, rgba(113,63,18,0.18) 0%, rgba(15,23,42,0.58) 100%)',
                        color: '#fef3c7',
                        textAlign: 'center',
                    }}
                >
                    <span
                        style={{
                            width: 30,
                            height: 30,
                            borderRadius: 999,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 8,
                            border: '1px solid rgba(250,204,21,0.5)',
                            background: 'rgba(250,204,21,0.14)'
                        }}
                    >
                        <i className="fas fa-lock" style={{ fontSize: '0.82rem', color: '#fef3c7' }} />
                    </span>
                    <p style={{ margin: 0, fontSize: '0.84rem', fontWeight: 800, color: '#fffbeb' }}>Totals locked</p>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.74rem', color: '#fcd34d' }}>
                        Unlock cost: {unlockCredits} credit{unlockCredits > 1 ? 's' : ''}
                    </p>
                    <p style={{ margin: '0.45rem 0 0', fontSize: '0.72rem', color: 'rgba(255,251,235,0.88)' }}>
                        Use the Unlock button in the header to view this totals window.
                    </p>
                </div>
            </InnerContent>
        );
    }

    if (!game.totalStats) {
        return (
            <InnerContent fluid>
                <NoDataMessage>
                    <i className="fas fa-equals" />
                    <p>There is currently no total data for this game. Check back soon.</p>
                </NoDataMessage>
            </InnerContent>
        );
    }

    const totalStats = game.totalStats;
    const totalGames = totalStats.under + totalStats.over;
    const overPercentage = totalGames > 0 ? Math.round((totalStats.over / totalGames) * 100) : 0;
    const underPercentage = totalGames > 0 ? Math.round((totalStats.under / totalGames) * 100) : 0;

    const getPredictionConfig = () => {
        switch (totalStats.prediction) {
            case 'OVER':
                return { color: '#22c55e', gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', label: 'OVER' };
            case 'UNDER':
                return { color: '#ef4444', gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', label: 'UNDER' };
            case 'PUSH':
                return { color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', label: 'PUSH' };
            default:
                return { color: '#6b7280', gradient: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)', label: 'N/A' };
        }
    };

    const predictionConfig = totalStats.prediction ? getPredictionConfig() : null;

    // Line movement
    const openingTotal = totalStats.openingTotal;
    const delta = openingTotal ? parseFloat((game.total - openingTotal).toFixed(1)) : 0;
    const isPositive = delta > 0;
    const isNegative = delta < 0;

    return (
        <InnerContent fluid>
            <StatsRow>
                <TotalContainer>
                    <TotalLabel>Total Line</TotalLabel>
                    <TotalText>{game.total}</TotalText>
                </TotalContainer>
                {predictionConfig && (
                    <PredictionCard gradient={predictionConfig.gradient}>
                        <PredictionLabel>AI Prediction</PredictionLabel>
                        <PredictionBadge>{predictionConfig.label}</PredictionBadge>
                    </PredictionCard>
                )}
            </StatsRow>

            {openingTotal > 0 && (
                <LineMovementContainer>
                    <LineMovementTitle>Line Movement</LineMovementTitle>
                    <LineMovementRow>
                        <LineMovementValue>
                            <LineMovementNumber>{openingTotal}</LineMovementNumber>
                            <LineMovementLabel>Opening</LineMovementLabel>
                        </LineMovementValue>
                        <LineMovementArrow>
                            <MovementIcon positive={isPositive} negative={isNegative}>
                                {isPositive ? '▲' : isNegative ? '▼' : '→'}
                            </MovementIcon>
                            <DeltaBadge positive={isPositive} negative={isNegative}>
                                {delta === 0 ? 'No Move' : `${isPositive ? '+' : ''}${delta}`}
                            </DeltaBadge>
                        </LineMovementArrow>
                        <LineMovementValue>
                            <LineMovementNumber large>{game.total}</LineMovementNumber>
                            <LineMovementLabel>Current</LineMovementLabel>
                        </LineMovementValue>
                    </LineMovementRow>
                </LineMovementContainer>
            )}

            <ProgressBarContainer>
                <SectionLabel>Historical Performance for Similar Games</SectionLabel>
                <ProgressLabels>
                    <ProgressLabel>
                        <LabelText>Overs</LabelText>
                        <LabelValue color={overPercentage >= underPercentage ? '#22c55e' : '#ef4444'}>{totalStats.over}</LabelValue>
                        <LabelPercentage>({overPercentage}%)</LabelPercentage>
                    </ProgressLabel>
                    <ProgressLabel>
                        <LabelPercentage>({underPercentage}%)</LabelPercentage>
                        <LabelValue color={underPercentage >= overPercentage ? '#22c55e' : '#ef4444'}>{totalStats.under}</LabelValue>
                        <LabelText>Unders</LabelText>
                    </ProgressLabel>
                </ProgressLabels>
                <ProgressBar>
                    <ProgressFill percentage={overPercentage} isHigher={overPercentage >= underPercentage} />
                    <ProgressFill percentage={underPercentage} isHigher={underPercentage >= overPercentage} />
                </ProgressBar>
            </ProgressBarContainer>

            <CompareList
                labels={sport === 'mlb' ? ['PPG', 'O/U'] : ['PPG', 'O/U', sport === 'nba' || sport === 'cbb' ? 'Pace Rank' : 'TOP Rank']}
                dataSetOne={generateFieldList(awayTeamData)}
                dataSetOneTitle={game.awayTeam.teamName}
                dataSetOneTitleColor={awayTeamData.primaryColor}
                dataSetTwo={generateFieldList(homeTeamData)}
                dataSetTwoTitle={game.homeTeam.teamName}
                dataSetTwoTitleColor={homeTeamData.primaryColor}
            />
            {sport === 'nba' || sport === 'cbb' ? (
                <RankHint>Lower Pace Rank = more possessions per game</RankHint>
            ) : sport !== 'mlb' ? (
                <RankHint>Lower TOP Rank = more time of possession</RankHint>
            ) : null}
        </InnerContent>
    );
};

Totals.propTypes = {
    sport: string.isRequired,
    game: shape({}).isRequired,
    isLocked: bool,
    unlockCredits: number,
};

export default Totals;
